import { useEffect, useState } from 'react';
import { PRESETS, StatisticsFilters, formatDay } from '../../utils/period';
import { getCampuses } from '../../services/statisticsService';

export interface PeriodInfo {
  from: string;
  to: string;
  days: number;
  previous: { from: string; to: string };
}

interface PeriodBarProps {
  filters: StatisticsFilters;
  onChange: (next: Partial<StatisticsFilters>) => void;
  /** The period the server actually used, once loaded. */
  period?: PeriodInfo | null;
  /** Hide the comparison line on screens that don't compare. */
  showComparison?: boolean;
}

type Campus = { id: string; name: string; members: number };
let campusCache: Campus[] | null = null;

/** Campuses that have students, loaded once per session. */
export function useCampuses(): Campus[] {
  const [campuses, setCampuses] = useState<Campus[]>(campusCache || []);
  useEffect(() => {
    if (campusCache) return;
    getCampuses()
      .then((items) => {
        campusCache = items;
        setCampuses(items);
      })
      .catch(() => setCampuses([]));
  }, []);
  return campuses;
}

/** Date range and campus, in one row above every statistics screen. */
const PeriodBar = ({ filters, onChange, period, showComparison = true }: PeriodBarProps) => {
  const campuses = useCampuses();
  const [from, setFrom] = useState(filters.from);
  const [to, setTo] = useState(filters.to);

  useEffect(() => {
    setFrom(filters.from);
    setTo(filters.to);
  }, [filters.from, filters.to]);

  const applyCustom = () => {
    if (from && to && from <= to) onChange({ preset: 'custom', from, to });
  };

  return (
    <div className="stats-period-bar">
      <div className="stats-period-controls">
        <label className="stats-control">
          <span>Period</span>
          <select
            value={filters.preset}
            onChange={(e) => {
              const preset = e.target.value;
              if (preset === 'custom') {
                onChange({ preset: 'custom', from: period?.from || '', to: period?.to || '' });
              } else {
                onChange({ preset, from: '', to: '' });
              }
            }}
          >
            {PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        {filters.preset === 'custom' && (
          <div className="stats-custom-range">
            <label className="stats-control">
              <span>From</span>
              <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="stats-control">
              <span>To</span>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button
              type="button"
              className="stats-button"
              onClick={applyCustom}
              disabled={!from || !to || from > to || (from === filters.from && to === filters.to)}
            >
              Apply
            </button>
          </div>
        )}

        <label className="stats-control">
          <span>Campus</span>
          <select value={filters.campus} onChange={(e) => onChange({ campus: e.target.value })}>
            <option value="">All campuses</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {period && (
        <p className="stats-period-summary">
          {formatDay(period.from)} – {formatDay(period.to)} ({period.days} days, Israel time)
          {showComparison && (
            <>
              {' '}
              · compared with {formatDay(period.previous.from)} – {formatDay(period.previous.to)}
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default PeriodBar;
